import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';
import '../styles/AuthForm.css';

const AuthForm = () => {
  const { t, language, switchLanguage } = useLanguage();
  const { login, register, loading, error, clearError } = useAuth();
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    confirmPassword: ''
  });
  const [formErrors, setFormErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear field-specific errors
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear auth errors
    if (error) {
      clearError();
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Email validation
    if (!formData.email) {
      errors.email = t('emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = t('invalidEmail');
    }
    
    // Password validation
    if (!formData.password) {
      errors.password = t('passwordRequired');
    } else if (formData.password.length < 6) {
      errors.password = t('passwordTooShort');
    }
    
    // Registration-specific validation
    if (!isLoginMode) {
      if (!formData.name) {
        errors.name = t('nameRequired');
      }
      
      if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = t('passwordsDoNotMatch');
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      let result;
      if (isLoginMode) {
        result = await login(formData.email, formData.password);
      } else {
        result = await register(formData.email, formData.password, formData.name);
      }
      
      if (result.success) {
        // Success handled by AuthContext
        setFormData({
          email: '',
          password: '',
          name: '',
          confirmPassword: ''
        });
      }
    } catch (err) {
      // Error handled by AuthContext
      console.error('Auth error:', err);
    }
  };

  const toggleMode = () => {
    setIsLoginMode(!isLoginMode);
    setShowForgotPassword(false);
    setShowResetPassword(false);
    setFormData({
      email: '',
      password: '',
      name: '',
      confirmPassword: ''
    });
    setFormErrors({});
    clearError();
  };

  const handleShowLogin = () => {
    setShowForgotPassword(false);
    setShowResetPassword(false);
    setIsLoginMode(true);
    setFormData({
      email: '',
      password: '',
      name: '',
      confirmPassword: ''
    });
    setFormErrors({});
    clearError();
  };

  const handleForgotPasswordSuccess = () => {
    setShowForgotPassword(false);
    setIsLoginMode(true);
  };

  const handleResetPasswordSuccess = () => {
    setShowResetPassword(false);
    setIsLoginMode(true);
  };

  // Check URL parameters for reset token
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      setShowResetPassword(true);
      setShowForgotPassword(false);
      setIsLoginMode(false);
    }
  }, []);

  // Show Forgot Password component
  if (showForgotPassword) {
    return (
      <ForgotPassword
        onBack={handleShowLogin}
        onShowLogin={handleShowLogin}
      />
    );
  }

  // Show Reset Password component
  if (showResetPassword) {
    return (
      <ResetPassword
        onSuccess={handleResetPasswordSuccess}
        onShowLogin={handleShowLogin}
      />
    );
  }

  return (
    <div className="auth-overlay">
      <div className="auth-form-container">
        <div className="auth-form">
          {/* Language Switcher for Auth Form */}
          <div className="auth-language-switcher">
            <button
              className={`auth-language-btn ${language === 'en' ? 'active' : ''}`}
              onClick={() => switchLanguage('en')}
              title="English"
              type="button"
            >
              🇺🇸
            </button>
            <button
              className={`auth-language-btn ${language === 'de' ? 'active' : ''}`}
              onClick={() => switchLanguage('de')}
              title="Deutsch"
              type="button"
            >
              🇩🇪
            </button>
          </div>
          
          <h2>{isLoginMode ? t('login') : t('register')}</h2>
          
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            {!isLoginMode && (
              <div className="form-group">
                <label htmlFor="name">{t('name')}</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t('namePlaceholder')}
                  className={formErrors.name ? 'error' : ''}
                />
                {formErrors.name && (
                  <span className="field-error">{formErrors.name}</span>
                )}
              </div>
            )}
            
            <div className="form-group">
              <label htmlFor="email">{t('email')}</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder={t('emailPlaceholder')}
                className={formErrors.email ? 'error' : ''}
              />
              {formErrors.email && (
                <span className="field-error">{formErrors.email}</span>
              )}
            </div>
            
            <div className="form-group">
              <label htmlFor="password">{t('password')}</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder={t('passwordPlaceholder')}
                className={formErrors.password ? 'error' : ''}
              />
              {formErrors.password && (
                <span className="field-error">{formErrors.password}</span>
              )}
            </div>
            
            {!isLoginMode && (
              <div className="form-group">
                <label htmlFor="confirmPassword">{t('confirmPassword')}</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder={t('confirmPasswordPlaceholder')}
                  className={formErrors.confirmPassword ? 'error' : ''}
                />
                {formErrors.confirmPassword && (
                  <span className="field-error">{formErrors.confirmPassword}</span>
                )}
              </div>
            )}
            
            <button 
              type="submit" 
              className="btn btn-primary auth-submit"
              disabled={loading}
            >
              {loading ? t('loading') : (isLoginMode ? t('loginBtn') : t('registerBtn'))}
            </button>
          </form>
          
          <div className="auth-toggle">
            <p>
              {isLoginMode ? t('noAccount') : t('hasAccount')}
              <button 
                type="button" 
                onClick={toggleMode}
                className="link-btn"
              >
                {isLoginMode ? t('registerLink') : t('loginLink')}
              </button>
            </p>
            
            {isLoginMode && (
              <p style={{ marginTop: '10px' }}>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="link-btn"
                >
                  {t('forgotPassword') || 'Passwort vergessen?'}
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthForm;